import { Link } from "react-router-dom";
import { SubPageShell, PageFaq, ProfessionalBadge } from "@/components/site/SubPage";
import { ComplianceCheckForm, ComplianceQuiz } from "@/components/site/ComplianceCheck";
import { useSeo } from "@/lib/useSeo";
import { scrollToId, whatsappUrl } from "@/lib/site";

const PACCHETTI = [
  {
    id: "checkup",
    t: "Check-up fiscale della tua struttura",
    d: "Regime, CIN, SCIA, imposta di soggiorno, contabilità dei portali: cosa è in ordine e cosa no, scritto in parole semplici.",
    price: "da 150 €",
    note: "Prezzo fisso, comunicato prima",
    cta: "Chiedi il check-up",
    wa: "Ciao, vorrei il check-up fiscale della mia struttura con Valerio.",
  },
  {
    id: "apertura",
    t: "Apertura completa",
    d: "Partita IVA, SCIA e CIN: tutto quello che serve per partire in regola, seguito da lui dall'inizio alla fine.",
    price: "da 300 €",
    note: "Prezzo definito sul tuo caso, per iscritto",
    cta: "Parti in regola",
    wa: "Ciao, sto per aprire una struttura e vorrei l'apertura completa con Valerio.",
  },
  {
    id: "gestione",
    t: "Gestione annuale",
    d: "Contabilità, dichiarazioni, adempimenti e rapporti con il comune, tutto l'anno. Tu vedi solo che è fatto.",
    price: "da 1.500 €/anno",
    note: "Canone annuale, senza sorprese",
    cta: "Chiedi un preventivo",
    wa: "Ciao, vorrei un preventivo per la gestione annuale con Valerio.",
  },
];

const FAQ = [
  {
    q: "Serve la partita IVA per affittare?",
    a: "Dipende da quanti immobili affitti e con che continuità: le regole cambiano molto da caso a caso. Valerio ti dice esattamente in quale situazione rientri tu, prima che tu spenda un euro.",
  },
  {
    q: "Cosa sono SCIA e CIN?",
    a: "Sono gli adempimenti per essere in regola: la SCIA è la segnalazione al comune che apri l'attività, il CIN è il Codice Identificativo Nazionale obbligatorio che va esposto nell'annuncio. Ce ne occupiamo noi.",
  },
  {
    q: "Devo cambiare il mio commercialista attuale?",
    a: "No. Valerio può lavorare insieme al tuo commercialista di fiducia, portando solo la specializzazione sulle strutture ricettive. Oppure seguire tutto lui: scegli tu.",
  },
  {
    q: "Quanto costa?",
    a: "Dipende da cosa ti serve davvero: prima capiamo la tua situazione in una chiamata gratuita, poi ricevi un preventivo chiaro. Nessun costo nascosto e nessun vincolo iniziale: decidi tu se e quando partire.",
  },
];

export default function CommercialistaPage() {
  useSeo({
    title: "Commercialista per affitti brevi in Sicilia | YU YieldUp",
    description:
      "Commercialista specializzato in affitti brevi, B&B e case vacanze in Sicilia: partita IVA, cedolare secca, SCIA, CIN e contabilità dei portali, con un team che conosce il settore dall'interno.",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "ProfessionalService",
          name: "Consulenza fiscale per affitti brevi e strutture ricettive",
          provider: { "@type": "Person", name: "Valerio Pavano" },
          areaServed: { "@type": "AdministrativeArea", name: "Sicilia" },
          description:
            "Consulenza fiscale e amministrativa per affitti brevi, B&B, case vacanze e strutture ricettive: partita IVA, cedolare secca, SCIA, CIN, imposte di soggiorno e contabilità dei portali.",
        },
        {
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        },
      ],
    },
  });

  return (
    <SubPageShell callSource="commercialista">
      <section data-testid="commercialista-page" className="mx-auto max-w-3xl px-4 pb-24 sm:px-6">
        <p className="section-kicker">Servizi in Sicilia</p>
        <h1 className="mt-4 font-heading text-3xl font-black uppercase leading-[1.02] tracking-tighter text-[#FAF7F2] sm:text-5xl">
          Commercialista per affitti brevi in Sicilia
        </h1>
        <p className="mt-6 text-base font-light leading-relaxed text-[#C4BBB0] sm:text-lg">
          Affitti brevi, B&B e case vacanze hanno regole fiscali tutte loro: cedolare secca, SCIA, CIN, imposta di soggiorno, contabilità dei portali. Un commercialista generalista spesso le scopre insieme a te. Uno specializzato le ha già gestite centinaia di volte.
        </p>
        <ProfessionalBadge testid="commercialista-badge" photo="/images/valerio.jpg" name="Valerio Pavano, commercialista" caption="Specializzato in strutture ricettive. Quello che ti tiene in regola." />
        <ComplianceCheckForm />

        <h2 className="mt-14 font-heading text-2xl font-black uppercase tracking-tight text-[#FAF7F2]">Di cosa ci occupiamo</h2>
        <ul className="mt-6 space-y-4">
          {[
            "Apertura e gestione della partita IVA per attività ricettive",
            "Cedolare secca o regime ordinario: la scelta giusta per il tuo caso, fatta sui numeri",
            "SCIA comunale, Codice Identificativo Nazionale e adempimenti per essere in regola",
            "Imposta di soggiorno e rapporti con il comune",
            "Contabilità delle prenotazioni da Booking, Airbnb e dagli altri portali: incassi puliti e riconciliati",
          ].map((item) => (
            <li key={item} className="flex gap-4 text-sm leading-relaxed text-[#C4BBB0] sm:text-base">
              <span className="shrink-0 font-heading font-black text-[#E05A2C]">—</span>
              {item}
            </li>
          ))}
        </ul>

        <h2 className="mt-14 font-heading text-2xl font-black uppercase tracking-tight text-[#FAF7F2]">Tre modi per lavorare con Valerio</h2>
        <div className="mt-6 grid gap-px bg-[#383029] sm:grid-cols-3">
          {PACCHETTI.map((p) => (
            <div key={p.id} data-testid={`commercialista-pacchetto-${p.id}`} className="flex flex-col bg-[#16110E] p-6">
              <p className="font-heading text-base font-black uppercase tracking-tight text-[#FAF7F2]">{p.t}</p>
              <p className="mt-3 flex-1 text-sm font-light leading-relaxed text-[#C4BBB0]">{p.d}</p>
              <div className="mt-5 border-t border-[#383029] pt-4">
                {p.price && <p data-testid={`commercialista-pacchetto-${p.id}-prezzo`} className="font-heading text-2xl font-black tracking-tighter text-[#E05A2C]">{p.price}</p>}
                <p className={`font-mono text-xs uppercase tracking-widest ${p.price ? "mt-1 text-[#A89F91]" : "text-[#E05A2C]"}`}>{p.note}</p>
              </div>
              <a
                href={whatsappUrl(p.wa)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`commercialista-pacchetto-${p.id}-cta`}
                className="mt-4 inline-flex min-h-[44px] items-center justify-center rounded-full border border-[#383029] px-5 py-2.5 text-sm font-medium text-[#FAF7F2] transition-colors duration-300 hover:border-[#E05A2C] hover:text-[#E05A2C]"
              >
                {p.cta}
              </a>
            </div>
          ))}
        </div>
        <p className="mt-3 font-mono text-xs uppercase tracking-widest text-[#686054]">
          Il preventivo lo ricevi per iscritto dopo la prima chiamata. Nessun costo nascosto.
        </p>

        <ComplianceQuiz />

        <h2 className="mt-14 font-heading text-2xl font-black uppercase tracking-tight text-[#FAF7F2]">Chi se ne occupa: Valerio Pavano</h2>
        <p className="mt-6 text-base font-light leading-relaxed text-[#C4BBB0]">
          Valerio è un commercialista specializzato in strutture ricettive. Ha seguito la parte amministrativa e contabile di hotel, resort e B&B in tutta Italia, nel settore alberghiero ed extralberghiero: contabilità generale e analitica, flussi di incasso dai grandi portali, rapporti con fornitori e pagamenti.
        </p>
        <p className="mt-4 text-base font-light leading-relaxed text-[#C4BBB0]">
          In pratica: la burocrazia smette di essere un tuo pensiero. Tu vedi solo che è fatta bene e nei tempi giusti.
        </p>

        <div className="mt-10 border border-[#4D2C20] bg-[#1A1714] p-6 sm:p-8">
          <p className="font-heading text-lg font-black uppercase tracking-tight text-[#FAF7F2]">Non sai se la tua situazione fiscale è quella giusta?</p>
          <p className="mt-3 text-sm font-light leading-relaxed text-[#C4BBB0] sm:text-base">
            Indica tipo di struttura, città e WhatsApp nel modulo in alto: entro 48 ore ti scriviamo e fissiamo 30 minuti con Valerio. Ti dice se sei in regola e se c'è un regime più conveniente per te.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <button
              data-testid="commercialista-cta-call"
              onClick={() => scrollToId("check-regola")}
              className="btn-glow min-h-[44px] rounded-full bg-[#E05A2C] px-7 py-3.5 text-sm font-medium text-white transition-colors duration-300 hover:bg-[#F26B3A] active:scale-[0.98]"
            >
              Fai il check con Valerio
            </button>
            <Link to="/" className="text-sm text-[#A89F91] underline decoration-[#383029] underline-offset-4 transition-colors duration-300 hover:text-[#E05A2C]">
              Scopri tutto il lavoro di YU YieldUp
            </Link>
          </div>
        </div>

        <h2 className="mt-14 font-heading text-2xl font-black uppercase tracking-tight text-[#FAF7F2]">Domande frequenti</h2>
        <PageFaq items={FAQ} />
      </section>
    </SubPageShell>
  );
}
